/**
 * scroll.js — единые правила программного скролла.
 *
 * Почему это отдельный модуль, а не три вызова по месту.
 *
 * Скролл-контейнер приложения — .main-content (overflow-y: scroll), а не
 * документ: .app-layout зафиксирован (position: fixed; overflow: hidden).
 * Значит любой браузерный «автоскролл к элементу» двигает именно ту область,
 * в которой юзер сейчас работает, — и делает это, поднимаясь по ВСЕЙ цепочке
 * предков, а не только по тому контейнеру, который мы имели в виду.
 *
 * Отсюда два класса багов, из-за которых экран уезжал во время ввода позиций:
 *
 *   1. element.scrollIntoView() внутри выпадающего списка подсказок.
 *      Мы хотели прокрутить сам список (держать подсвеченную строку видимой),
 *      а браузер заодно прокручивал .main-content — и страница уезжала под
 *      юзером, хотя список и так был на экране. На телефоне, где список
 *      раскрывается ВВЕРХ над клавиатурой, страница уезжала вверх.
 *      → scrollWithin(): двигаем scrollTop только заданного контейнера.
 *
 *   2. el.focus() без preventScroll рядом с scrollIntoView().
 *      focus() сам по себе прокручивает предков к элементу — мгновенно и
 *      немедленно, обрывая запущенную рядом smooth-анимацию scrollIntoView
 *      на середине. Два скролла с разными целями = видимый прыжок.
 *      → focusQuiet(): фокус без скролла, скролл — отдельным осознанным
 *        вызовом revealRow() и ровно один.
 *
 * Правило по проекту: программный фокус ставим ТОЛЬКО через focusQuiet, а
 * показываем элемент ТОЛЬКО через revealRow/scrollWithin. Прямой
 * scrollIntoView оставляем для осознанной навигации «перебрось меня к
 * карточке» (клик по ссылке, автораскрытие блока), где движение экрана —
 * и есть цель.
 */

/**
 * Показать элемент в пределах ОДНОГО контейнера, не трогая предков.
 * Минимальным сдвигом: если элемент уже виден — не делаем ничего.
 *
 * @param {HTMLElement|null} container прокручиваемый контейнер (overflow: auto/scroll)
 * @param {HTMLElement|null} el        элемент внутри него
 */
export function scrollWithin(container, el) {
  if (!container || !el) return;
  const c = container.getBoundingClientRect();
  const r = el.getBoundingClientRect();
  if (r.top < c.top) {
    container.scrollTop -= (c.top - r.top);
  } else if (r.bottom > c.bottom) {
    container.scrollTop += (r.bottom - c.bottom);
  }
}

/**
 * То же по ГОРИЗОНТАЛИ: показать элемент в пределах одного контейнера.
 *
 * Отдельная функция, а не флаг у scrollWithin: у горизонтальных лент своя
 * причина существовать — они живут внутри вертикально прокручиваемого экрана,
 * и любой браузерный «покажи элемент» тут двигает ОБЕ оси. Лента кадров в
 * камере от такого уезжала бы вместе со всей страницей под пальцем.
 *
 * Минимальным сдвигом: элемент уже виден — не делаем ничего. Плюс запас в
 * несколько пикселей, иначе последний кадр прилипает к самой кромке и
 * выглядит обрезанным.
 *
 * @param {HTMLElement|null} container прокручиваемый контейнер (overflow-x: auto/scroll)
 * @param {HTMLElement|null} el        элемент внутри него
 * @param {number}           pad       зазор от кромки, px
 */
export function scrollWithinX(container, el, pad = 6) {
  if (!container || !el) return;
  const c = container.getBoundingClientRect();
  const r = el.getBoundingClientRect();
  if (r.left < c.left + pad) {
    container.scrollLeft -= (c.left + pad - r.left);
  } else if (r.right > c.right - pad) {
    container.scrollLeft += (r.right - c.right + pad);
  }
}

/**
 * Фокус без браузерного автоскролла.
 * preventScroll поддержан во всех целевых браузерах; try/catch — страховка на
 * старом WebView, где опция игнорируется и лучше получить обычный фокус, чем
 * исключение.
 *
 * @param {HTMLElement|null} el
 */
export function focusQuiet(el) {
  if (!el) return;
  try {
    el.focus({ preventScroll: true });
  } catch {
    el.focus();
  }
}

/**
 * Подтянуть элемент в зону видимости минимальным движением.
 *
 * block/inline: 'nearest' — принципиально, не 'center'. 'center' двигает экран
 * ВСЕГДА, даже когда элемент уже полностью виден: строка, которую юзер только
 * что заполнил, уезжала на середину экрана без всякой причины. 'nearest' не
 * делает ничего, если двигать нечего.
 *
 * Скролл мгновенный, не smooth: движение здесь короткое (в пределах одной
 * строки), а анимацию всё равно обрывает любой следующий фокус или ввод.
 *
 * @param {HTMLElement|null} el
 */
export function revealRow(el) {
  if (!el) return;
  el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
}

/**
 * Поднять элемент К ВЕРХУ контейнера, не трогая предков.
 *
 * В отличие от revealRow это ОСОЗНАННОЕ движение экрана, а не «дотяни
 * минимально»: на телефоне новой строке нужно всё место под собой — там
 * встанут и подсказки прайса, и экранная клавиатура. 'nearest' оставил бы
 * строку у нижней кромки, где не помещается ни то, ни другое.
 *
 * Браузер сам зажимает scrollTop по максимуму: если ниже строки контента нет,
 * она поднимется настолько, насколько возможно.
 *
 * @param {HTMLElement|null} container прокручиваемый контейнер
 * @param {HTMLElement|null} el        элемент внутри него
 * @param {number}           offset    зазор сверху, px
 */
export function scrollRowToTop(container, el, offset = 8) {
  if (!container || !el) return;
  const c = container.getBoundingClientRect();
  const r = el.getBoundingClientRect();
  container.scrollTop += (r.top - c.top) - offset;
}

/**
 * Типовой сценарий «завели новую строку»: курсор в её первое поле, строку —
 * в зону видимости. Порядок важен: сначала фокус БЕЗ скролла, потом ровно один
 * скролл. В обратном порядке focus() обрывал бы наш же скролл.
 *
 * @param {HTMLElement|null} row   контейнер строки
 * @param {HTMLElement|null} input поле, которому отдать курсор (по умолчанию первый input строки)
 */
export function focusRow(row, input) {
  if (!row) return;
  focusQuiet(input || row.querySelector('input'));
  revealRow(row);
}
